import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import debug from 'debug';
import { useCallback, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useSocket } from '@/hooks/useSocket';
import { useTrackMessageAnalytics, } from '@/hooks/useTrackMessageAnalytics';
import { References } from '@/newComponents/References/References';
import { isTestSelector, maxReferencesSelector } from '@/state/config';
import { isLastMessageFromBrainSelector, isResponseDisabledSelector, showExternalUrlsSelector, showMessageFeedbackSelector, } from '@/state/session';
import { sleep } from '@/util/utils';
import { Audio } from '../Audio';
import { CustomCarousel } from '../Carousel/Carousel';
import { FeedbackMessage } from '../FeedbackMessage/FeedbackMessage';
import { File } from '../File';
import { Image } from '../Image';
import { Options } from '../Options/Options';
import { Survey } from '../Survey';
import { Text } from '../Text';
import { Url } from '../Url';
import { Video } from '../Video';
import { WebviewResponse } from '../Webview/WebviewResponse';
const log = debug('moveo:response-event');
export const BrainResponseEvent = ({ event }) => {
    var _a, _b;
    const { sendMessageFeedback, sendMessage } = useSocket();
    const isTest = useRecoilValue(isTestSelector);
    const showFeedBackBubble = useRecoilValue(showMessageFeedbackSelector(event.event_id));
    const { trackMessageClick } = useTrackMessageAnalytics();
    const [isVoteComplete, setIsVoteComplete] = useState(false);
    const isDisabled = useRecoilValue(isResponseDisabledSelector(event.event_id));
    const message = event.response;
    const [clicked, setClicked] = useState(null);
    const maxReferences = useRecoilValue(maxReferencesSelector);
    const shouldShowExternalUrls = useRecoilValue(showExternalUrlsSelector(event.event_id));
    const isLastMessageFromBrain = useRecoilValue(isLastMessageFromBrainSelector(event.request_id));
    const handleReactionClick = useCallback(async (value) => {
        trackMessageClick(event, {
            type: 'reaction',
            value,
        });
        const request_id = event === null || event === void 0 ? void 0 : event.request_id;
        setClicked(value);
        sendMessageFeedback(request_id, value);
        await sleep(1000);
        setIsVoteComplete(true);
    }, [event, sendMessageFeedback, trackMessageClick]);
    const handleButtonClick = (data) => {
        trackMessageClick(event, data);
    };
    const showAnimation = event.source === 'live' && isLastMessageFromBrain;
    const renderResponse = () => {
        switch (message.type) {
            case 'text':
            case 'disambiguation':
                return _jsx(Text, { message: message, showAnimation: showAnimation });
            case 'url':
                return _jsx(Url, { url: message.url, onClick: handleButtonClick });
            case 'video':
                return _jsx(Video, { url: message.url, name: message.name });
            case 'image':
                return _jsx(Image, { url: message.url, name: message.name });
            case 'file':
                return _jsx(File, { url: message.url, name: message.name });
            case 'audio':
                return _jsx(Audio, { url: message.url });
            case 'carousel':
                return (_jsx(CustomCarousel, { message: message, disabled: isDisabled, onButtonClick: handleButtonClick }));
            case 'pause':
                return null;
            case 'webview':
                return (_jsx(WebviewResponse, { webview: message, onButtonClick: handleButtonClick }));
            case 'survey':
                return _jsx(Survey, { onButtonClick: handleButtonClick, survey: message });
            default:
                log('message-not-supported', message);
        }
        return null;
    };
    const handleOptionClick = useCallback((text, label) => {
        trackMessageClick(event, { type: 'option', value: text, label });
        sendMessage({ text, label });
    }, [event, sendMessage, trackMessageClick]);
    const hasOptions = 'options' in message;
    const external_urls = (_b = (_a = event === null || event === void 0 ? void 0 : event.collection) === null || _a === void 0 ? void 0 : _a.external_urls) === null || _b === void 0 ? void 0 : _b.filter((url) => !!url.name && !!url.external_url);
    return (_jsxs("li", { "data-event-type": "brain_message", className: cn('relative mt-4', {
            'inline-flex w-full flex-wrap': showFeedBackBubble,
        }), children: [renderResponse(), message && hasOptions && (_jsx(Options, { options: message.options, disabled: isDisabled, handleOptionClick: handleOptionClick })), maxReferences && shouldShowExternalUrls && (_jsx(References, { urls: external_urls, maxReferences: maxReferences, onClick: handleButtonClick })), showFeedBackBubble && !isVoteComplete && !isTest && (_jsx(FeedbackMessage, { clicked: clicked, handleClick: handleReactionClick }))] }));
};
